import { Link } from "react-router-dom";
import { Button } from "antd";
import { Container, SIGN_IN_PATH } from "./primitives";
import { useOpenContact } from "./ContactModal";

type FooterLink = { label: string; to?: string; contact?: boolean };

// Links without `to` have no destination in the app yet, so they render as text.
const COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: "Product",
    links: [
      { label: "Dashboard", to: SIGN_IN_PATH },
      { label: "Medications" },
      { label: "Lab Results" },
      { label: "Mobile App" },
      { label: "Pricing" },
    ],
  },
  {
    title: "Company",
    links: [{ label: "About Us", to: "/about" }, { label: "Blog" }, { label: "Careers" }, { label: "Press Kit" }, { label: "Contact", contact: true }],
  },
  {
    title: "Compliance",
    links: [
      { label: "HIPAA", to: "/hipaa-notice" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Service", to: "/terms-of-use" },
      { label: "Data Processing" },
      { label: "Certifications" },
    ],
  },
  {
    title: "Support",
    links: [{ label: "Help Center" }, { label: "Status" }, { label: "Security" }, { label: "Community" }, { label: "API Docs" }],
  },
];

const LEGAL_LINKS: FooterLink[] = [
  { label: "Privacy", to: "/privacy-policy" },
  { label: "Terms", to: "/terms-of-use" },
  { label: "Cookies" },
];

const BADGES = ["HIPAA", "SOC 2", "HITRUST"];

function FooterItem({ link, className }: { link: FooterLink; className: string }) {
  const openContact = useOpenContact();
  if (link.to) return <Link to={link.to} className={`${className} hover:text-white`}>{link.label}</Link>;
  if (link.contact) {
    return (
      <Button type="link" onClick={() => openContact("contact")} className={`!h-auto !p-0 !text-inherit hover:!text-white ${className}`}>
        {link.label}
      </Button>
    );
  }
  return <span className={className}>{link.label}</span>;
}

export function LandingFooter() {
  return (
    <footer className="bg-[#0A0128]">
      <Container className="pt-[74px] pb-[44px]">
        <div className="grid gap-12 md:grid-cols-4 lg:grid-cols-[383px_repeat(4,minmax(0,1fr))] lg:gap-0">
          <div className="md:col-span-4 lg:col-span-1">
            <span className="flex items-center gap-2.5">
              <span className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-white"><img src="/logo-icon.png" alt="" aria-hidden="true" className="h-[26px] w-[26px] object-contain" /></span>
              <span>
                <span className="block text-[15px] font-bold leading-[18px] text-[#F18095]">MedSyncIQ</span>
                <span className="block text-[13px] leading-4 text-white">Healthcare Platform</span>
              </span>
            </span>
            <p className="!mt-6 text-[14px] leading-6 text-white/60">
              Every hospital record. Every format.
              <br />
              One place, automatically.
            </p>
            <div className="mt-6 flex gap-2">
              {BADGES.map(b => (
                <span key={b} className="flex h-[26px] items-center rounded-md border border-white/15 px-2.5 text-[10.5px] font-bold tracking-[0.06em] text-white/50">
                  {b}
                </span>
              ))}
            </div>
          </div>

          {COLUMNS.map(col => (
            <div key={col.title}>
              <p className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-white/70">{col.title}</p>
              <ul className="!mt-[22px] space-y-[13px]">
                {col.links.map(link => (
                  <li key={link.label}>
                    <FooterItem link={link} className="text-[14px] text-white/55 transition-colors" />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-[70px] flex flex-col gap-4 border-t border-white/10 pt-[30px] sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-white/45">© {new Date().getFullYear()} MedSyncIQ, Inc. All rights reserved.</p>
          <div className="flex gap-6">
            {LEGAL_LINKS.map(link => (
              <FooterItem key={link.label} link={link} className="text-[13px] text-white/45 transition-colors" />
            ))}
          </div>
        </div>
      </Container>
    </footer>
  );
}
